import React from "react";
import PropTypes from "prop-types";
import { AppUtils, SignolUtils } from "@src/utils";
import { COMMON_CONTROLS } from "@src/stories/utils";
import { useAuthContext } from "@src/context";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { Icon, NotificationBadge } from "../atoms";

/** Inner component for each nav item */
const MenuItem = ({
  icon,
  iconActive,
  title,
  onSelect,
  isActive = false,
  isOpen = false,
  disableHoverStyles = false,
  isNotificationBadge = false,
}) => {
  const iconName = isActive && !!iconActive ? iconActive : icon;
  if (!iconName) {
    return;
  }

  const elemIcon = isNotificationBadge ? (
    <NotificationBadge isActive={isActive} />
  ) : (
    <Icon name={iconName} size="lg" inline className="flex-shrink-0" />
  );

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full px-4 flex items-center text-black text-left cursor-pointer"
      title={isOpen ? "" : title}
    >
      <div /* Pill */
        className={`
          w-full px-2 py-2 rounded-md
          transition-colors duration-150
          flex items-start
          ${isActive ? "bg-primary-100" : disableHoverStyles ? "" : "hover:bg-neutral-100"}
        `}
      >
        {elemIcon}

        <span
          className={`
          overflow-hidden whitespace-nowrap flex-grow
          transition-[opacity,margin,transform] duration-200 ease-out transform-gpu
          ${
            isOpen
              ? "text-base opacity-100 ml-3 translate-x-0"
              : "opacity-0 ml-0 -translate-x-1 md:text-[0px]"
          }
        `}
        >
          {title}
        </span>
      </div>
    </button>
  );
};

/** Vertical Navigation Menu ("Rail"). Always visible as icons, text labels can be toggled
 * (Note: Tablet+Mobile width layouts not fully supported eg. Profile section in Mobile)
 */
const NavMenu2026 = ({
  positioning = "outside-left", //"outside-left" | "inside-left"
  items = [],
  currentPage = "",
  navMenuOpen = false,
  setNavMenuOpen,
  onItemSelect,
  onImpersonatedSession,
}) => {
  const auth = useAuthContext();
  const { avatarInitials, user } = auth;

  const onSwitchRole = () =>
    !!auth.hasRoleSwitcher() ? auth.selectUserProfile : false;

  return (
    <nav
      className={`
        top-0 z-50 inset-y-0 group
        transition-all duration-100 ease-out
        ${positioning === "outside-left" ? "absolute left-[-72px]" : "absolute left-0"}
        ${navMenuOpen ? "w-72" : "w-[72px]"}
      `}
    >
      <div className="bg-white border-gray-100 border-r-2 h-full">
        <div className="sticky top-16 pt-4 pb-8 space-y-2">
          <>
            <MenuItem
              title={null}
              icon={navMenuOpen ? "burgerBarsCollapseLeft" : "burgerBars"}
              isActive={false}
              onSelect={() => setNavMenuOpen(!navMenuOpen)}
              disableHoverStyles
            />
            {items
              .map((item, idx) => {
                if (item.separator) {
                  return (
                    <hr key={idx} className="m-4 border border-neutral-200" />
                  );
                }
                if (item.pageTitle && !item.hidden) {
                  const isActive = currentPage === item.path;
                  return (
                    <MenuItem
                      key={idx}
                      title={item.pageTitle}
                      icon={item.menuIcon}
                      iconActive={item.menuIconActive}
                      isActive={isActive}
                      onSelect={() => onItemSelect(item.path)}
                      isOpen={navMenuOpen}
                      isNotificationBadge={item.path.includes("notifications")}
                    />
                  );
                }
              })
              .filter(Boolean)}
          </>

          {/* Mobile only, show a menu version of the Profile section */}
          <div className="md:hidden py-8 border-t border-gray-200">
            <div className="flex items-center px-6">
              <div className="flex-shrink-0">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500">
                  <span className="text-lg font-bold leading-none text-white">
                    {avatarInitials}
                  </span>
                </span>
              </div>
              <div className="ml-3">
                <div className="flex space-x-2 text-sm leading-6 text-gray-800">
                  {/* <span className="capitalize">{currentRole}</span> */}
                  <span className="capitalize">
                    {user.firstname && user.lastname
                      ? `${user.firstname} ${user.lastname}`
                      : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <MenuItem
                title="Profile"
                icon="about"
                onSelect={() => onItemSelect("profile")}
                isActive={currentPage === "profile"}
                isOpen={navMenuOpen}
              />
              {!!onSwitchRole && (
                <MenuItem
                  title="Switch Role"
                  icon="logout"
                  onSelect={() => onSwitchRole(null)}
                  isActive={false}
                  isOpen={navMenuOpen}
                />
              )}
              {!!onImpersonatedSession && (
                <MenuItem
                  title="Logout from User"
                  icon="logout"
                  onSelect={() => onImpersonatedSession()}
                  isActive={false}
                  isOpen={navMenuOpen}
                />
              )}
              <MenuItem
                title="Logout"
                icon="logout"
                onSelect={() => auth.logout()}
                isActive={false}
                isOpen={navMenuOpen}
              />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export const NavMenu2026Controls = {
  positioning: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["outside-left", "inside-left"],
    description:
      "where to position the nav menu, inside or outside of the parent container (default outside left)",
  },
  items: {
    propType: PropTypes.arrayOf(PropTypes.object),
    description: "menu list items",
  },
  currentPage: {
    description: "eg. 'overview'",
    propType: PropTypes.string.isRequired,
    controlType: "text",
  },
  navMenuOpen: {
    propType: PropTypes.bool.isRequired,
    controlType: "boolean",
  },
  setNavMenuOpen: {
    propType: PropTypes.func,
    disable: true,
  },
  onItemSelect: {
    propType: PropTypes.func,
    disable: true,
  },
  onImpersonatedSession: {
    propType: PropTypes.func,
    disable: true,
  },
};
NavMenu2026.propTypes = AppUtils.getPropTypeFromControls(NavMenu2026Controls);

export default NavMenu2026;
